import React, { useCallback, useEffect, useState } from 'react';

import { Icons, SpinnerOverlay } from '@components/common';
import Dropzone from '@components/common/Dropzone';
import NavigateButtons from '@components/common/NavigateButtons';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import { IFilesFiles } from '@interfaces/common/file';
import { IErrorsType, TFileTypes } from '@interfaces/import/import';

import { ESnackbarTypes } from '@enums/common';
import { EImportingDataType, TImportingDataType } from '@enums/dropzone';

import { polygonFileTypesWithoutMapping } from '@constants/common';
import { responseMessages } from '@constants/responseMessages';
import { componentNames } from '../constants';
import { MAX_FILES, uploadedText } from './constants';

import styles from './UploadFile.module.scss';

interface IUploadFileProps {
	acceptedFileTypes: TFileTypes;
	createUploadUrl: ({ files }: IFilesFiles) => void;
	downloadSample?: () => void;
	fileNames: string[];
	importIds: string[];
	importType: TImportingDataType;
	isUploadingFile: boolean;
	onClose: () => void;
	setCurrentComponent: React.Dispatch<React.SetStateAction<string>>;
	setFileNames: React.Dispatch<React.SetStateAction<string[]>>;
	setImportIds: React.Dispatch<React.SetStateAction<string[]>>;
	supportedFilesDescription?: string;
	uploadDescription?: string;
	chooseFileText?: string;
	chooseFileDescription?: string;
	nextButtonText?: string;
	closeButtonText?: string;
	errors: IErrorsType;
	isDisabledNext?: boolean;
	errorMessage?: string;
	maxFiles?: number;
	disabledNext?: boolean;
	children?: React.ReactNode;
	disabled?: boolean;
}

const UploadFile = ({
	acceptedFileTypes,
	createUploadUrl,
	downloadSample,
	fileNames,
	importIds,
	importType,
	isUploadingFile,
	onClose,
	setCurrentComponent,
	setFileNames,
	setImportIds,
	supportedFilesDescription,
	uploadDescription,
	errors,
	chooseFileText,
	chooseFileDescription,
	nextButtonText,
	closeButtonText,
	isDisabledNext,
	errorMessage,
	maxFiles = MAX_FILES,
	disabledNext = false,
	children,
	disabled = false,
}: IUploadFileProps) => {
	const dispatch = useAppDispatch();

	const [isFileEmpty, setIsFileEmpty] = useState(true);
	const isLoadingFileToGcloud = !importIds?.length && fileNames?.length > 0;
	const isWithoutFieldsMapping =
		importType === EImportingDataType.polygon &&
		polygonFileTypesWithoutMapping.some((type) =>
			fileNames.some((fileName) => fileName.includes(type)),
		);

	const handleNextClick = () => {
		setCurrentComponent(
			importType === EImportingDataType.asset || importType === EImportingDataType.site
				? componentNames.importRules
				: isWithoutFieldsMapping
					? componentNames.resultStatistic
					: componentNames.fieldsMapping,
		);
	};

	const onClickDownload = () => downloadSample && downloadSample();

	const handleSetFileNames = useCallback(
		(files: File[]) => {
			const fileNamesList = files.map((file) => file.name);

			setFileNames(fileNamesList);

			setIsFileEmpty(files.length === 0 || !!isDisabledNext);

			if (files.some((file) => file.size === 0)) {
				dispatch(
					setSnackbar({
						message: responseMessages.emptyFile,
						type: ESnackbarTypes.error,
					}),
				);
			}
		},
		[dispatch, isDisabledNext, setFileNames],
	);

	useEffect(() => {
		if (isDisabledNext !== undefined) {
			setIsFileEmpty(isDisabledNext);
		}
	}, [isDisabledNext]);

	const clearUploadId = () => setImportIds([]);

	const fileRejectionErrorHandler = () => {
		setFileNames([]);
		setIsFileEmpty(true);
		clearUploadId();
	};

	return (
		<div className={styles.uploadFileContainer}>
			{(isUploadingFile || isLoadingFileToGcloud) && <SpinnerOverlay />}
			<Dropzone
				acceptedFileTypes={acceptedFileTypes}
				chooseFileDescription={chooseFileDescription}
				chooseFileText={chooseFileText}
				createUploadUrl={createUploadUrl}
				disabled={disabled}
				errorMessage={errorMessage}
				errors={errors}
				fileRejectionErrorHandler={fileRejectionErrorHandler}
				maxFiles={maxFiles}
				onDropAcceptedHandler={clearUploadId}
				setSelectedFiles={handleSetFileNames}
				supportsText={supportedFilesDescription}
				uploadIcon={Icons.uploadFile}
				uploadedIcon={Icons.uploadedFile}
				uploadedText={uploadedText}
			/>
			{downloadSample && (
				<div className={styles.sampleWrapper}>
					<div
						className={styles.download}
						onClick={onClickDownload}
					>
						Download sample file
					</div>
				</div>
			)}
			{uploadDescription && (
				<div className={styles.description}>{uploadDescription}</div>
			)}
			<div className={styles.childrenWrapper}>
				{children}
			</div>
			<div className={styles.buttons}>
				<NavigateButtons
					closeText={closeButtonText}
					disabledNext={disabledNext || isFileEmpty}
					nextText={nextButtonText}
					onCloseHandler={onClose}
					onNextHandler={handleNextClick}
				/>
			</div>
		</div>
	);
};

export default UploadFile;
